import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useCan } from '../contexts/AuthContext';
import VolumeSection from '../components/VolumeSection';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ConflictDialog from '../components/ConflictDialog';
import PresenceBadge from '../components/PresenceBadge';
import usePresence from '../hooks/usePresence';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import FastInput from '../components/FastInput';
import { Textarea } from '../components/ui/textarea';
import { FastTextarea } from '../components/FastInput';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    ArrowLeft,
    Save,
    ChevronUp,
    ChevronDown,
    Phone,
    Mail,
    User,
    MapPin,
    Calendar as CalendarIcon,
    Building,
    FileText,
    Layers,
    Target,
    Truck,
    Check,
    Send,
    Package,
    Clock,
    DollarSign,
    Plus,
    Trash2,
    Calculator,
    Gift,
    CheckCircle2
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import useFormDraft from '../hooks/useFormDraft';
import { useResilientSave, isNetworkError } from '../components/OfflineSavePrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUTS = [
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { value: 'sans_reponse_1', label: 'Sans réponse 1', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { value: 'sans_reponse_2', label: 'Sans réponse 2', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { value: 'sans_reponse_3', label: 'Sans réponse 3', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { value: 'a_rappeler', label: 'À rappeler', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { value: 'visite_virtuelle', label: 'Visite virtuelle', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { value: 'visite_presentiel', label: 'Visite présentiel', color: 'bg-green-100 text-green-700 border-green-200' },
    { value: 'devis_estimatif', label: 'Devis estimatif', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    { value: 'listing_attente', label: 'Listing en attente', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    { value: 'listing_telephone', label: 'Listing téléphone', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'listing_email', label: 'Listing email', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { value: 'faux_contact', label: 'Faux contact', color: 'bg-red-100 text-red-700 border-red-200' },
    { value: 'non_interessant', label: 'Non intéressant', color: 'bg-gray-100 text-gray-600 border-gray-200' },
];

const SOURCES = [
    { value: 'appel_simple', label: 'Appel simple' },
    { value: 'appel_google', label: 'Appel Google' },
    { value: 'formulaire_site', label: 'Formulaire site' },
    { value: 'message_google', label: 'Message Google' },
    { value: 'recommandation', label: 'Recommandation' },
    { value: 'ancien_client', label: 'Ancien client' },
    { value: 'plateforme_externe', label: 'Plateforme externe' },
];

const DEPARTEMENTS = [
    { value: '75', label: '75 - Paris' },
    { value: '77', label: '77 - Seine-et-Marne' },
    { value: '78', label: '78 - Yvelines' },
    { value: '91', label: '91 - Essonne' },
    { value: '92', label: '92 - Hauts-de-Seine' },
    { value: '93', label: '93 - Seine-Saint-Denis' },
    { value: '94', label: '94 - Val-de-Marne' },
    { value: '95', label: "95 - Val-d'Oise" },
    { value: 'hors_idf', label: 'Hors Île-de-France' },
];

const ASCENSEURS = [
    { value: 'non', label: 'Non' },
    { value: '1_3', label: '1 à 3 personnes' },
    { value: '4_6', label: '4 à 6 personnes' },
    { value: '7_9', label: '7 à 9 personnes' },
    { value: '10_plus', label: '10 personnes et plus' },
    { value: 'indetermine', label: 'Indéterminé' },
    { value: 'interdit', label: 'Interdit' },
];

const OUI_NON = [
    { value: 'oui', label: 'Oui' },
    { value: 'non', label: 'Non' },
];

const FORMULES = [
    { value: 'economique', label: 'Economique' },
    { value: 'economique_plus', label: 'Economique plus' },
    { value: 'standard', label: 'Standard' },
    { value: 'luxe', label: 'Luxe' },
];

const FORMULE_COLUMNS = [
    { key: 'economique', label: 'ECONOMIQUE', color: 'text-green-600', border: 'border-l-green-500' },
    { key: 'economique_plus', label: 'ECO. PLUS', color: 'text-amber-600', border: 'border-l-amber-500' },
    { key: 'standard', label: 'STANDARD', color: 'text-blue-600', border: 'border-l-blue-500' },
    { key: 'luxe', label: 'LUXE', color: 'text-red-500', border: 'border-l-red-400' },
];

const GARDE_MEUBLE_OPTIONS = [
    { value: 'non', label: 'Non' },
    { value: 'oui', label: 'Oui' },
    { value: 'a_definir', label: 'A definir' },
];

const emptyFormData = {
    nom: '',
    telephone: '',
    email: '',
    source: '',
    departement: '',
    statut: 'nouveau',
    adresse_depart: {
        date: null,
        adresse: '',
        etage: '',
        ascenseur: '',
        portage: '',
        autorisation_stationnement: '',
        monte_meuble: ''
    },
    adresse_arrivee: {
        date: null,
        adresse: '',
        etage: '',
        ascenseur: '',
        portage: '',
        autorisation_stationnement: '',
        monte_meuble: ''
    },
    remarques: ''
};

function AccordionSection({ title, isOpen, onToggle, children, completed, headerAction }) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between p-5 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className={`w-1.5 h-8 rounded-full transition-colors ${completed ? 'bg-green-500' : 'bg-[#C9A962]'}`}></div>
                            <h3 className="text-lg font-semibold text-[#1B365D]">{title}</h3>
                            {completed && <Check className="w-5 h-5 text-green-500" />}
                            {headerAction && <div onClick={(e) => e.stopPropagation()}>{headerAction}</div>}
                        </div>
                        {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                    </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="border-t border-gray-100">
                        <div className={`border-l-4 ml-5 transition-colors ${completed ? 'border-green-500' : 'border-[#C9A962]'}`}>
                            {children}
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}

function FormRow({ label, icon: Icon, children, required = false }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-center py-5 px-6 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3 sm:w-1/3 mb-2 sm:mb-0">
                {Icon && <Icon className="w-5 h-5 text-[#1B365D]/50" />}
                <span className="text-[#1B365D] font-medium">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </span>
            </div>
            <div className="sm:w-2/3">
                {children}
            </div>
        </div>
    );
}

export default function ProspectForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEditing = !!id;
    const { canWrite: canWriteProspect } = useCan('prospects');
    
    // Visit planning params from CarteRDV
    const visitDate = searchParams.get('visit_date');
    const visitStart = searchParams.get('visit_start');
    const visitEnd = searchParams.get('visit_end');
    const visitType = searchParams.get('visit_type');
    const visitAddress = searchParams.get('address');
    const hasVisitParams = !!(visitDate && visitType);
    const isExtended = searchParams.get('context') === 'extended';
    const fromPage = searchParams.get('from');
    const fromEventId = searchParams.get('event_id');
    const backUrl = fromEventId ? `/agenda/${fromEventId}` : (fromPage === 'agenda' ? '/agenda' : fromPage === 'inventaire' ? '/inventaire' : fromPage === 'devis' ? '/devis' : hasVisitParams ? '/agenda' : '/prospects');
    
    const [formData, setFormData] = useState(() => {
        const initial = { ...emptyFormData };
        if (visitAddress) {
            initial.adresse_depart = { ...initial.adresse_depart, adresse: visitAddress };
        }
        if (visitType) {
            initial.statut = visitType;
        }
        return initial;
    });
    // Snapshot of the very first formData so the draft hook can ignore
    // "drafts" that are still byte-identical to the form's pristine state.
    // Captured once with useRef so it doesn't drift as the user types.
    const pristineFormDataRef = useRef(null);
    if (pristineFormDataRef.current === null) {
        pristineFormDataRef.current = JSON.parse(JSON.stringify(formData));
    }
    const [initialFormData, setInitialFormData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(isEditing);
    const [emailConfirm, setEmailConfirm] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [showExitDialog, setShowExitDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [agendaSummary, setAgendaSummary] = useState({});

    // Validation "Visite terminée"
    const sectionRefs = useRef({});
    const volumeSectionRef = useRef(null);
    // Stable ref to handleSubmit so children (VolumeSection memo'd) don't
    // re-render every keystroke just because their `onExternalSave` prop is
    // a new arrow function each render.
    const handleSubmitRef = useRef(null);
    const stableExternalSave = useCallback(
        (opts = {}) => handleSubmitRef.current?.(opts),
        []
    );
    const [incompleteDialog, setIncompleteDialog] = useState({ open: false, sections: [] });
    const [successDialog, setSuccessDialog] = useState(false);
    const [inventaireItemsCount, setInventaireItemsCount] = useState(null);
    // Optimistic locking — last known updated_at + conflict modal state
    const [updatedAt, setUpdatedAt] = useState(null);
    const [conflict, setConflict] = useState(null); // { modified_by_name, current_updated_at, pendingPayload, isSilent }
    
    // Accordion states
    const [openSections, setOpenSections] = useState({
        source: true,
        contact: true,
        depart: !!visitAddress || isExtended,
        arrivee: isExtended,
        remarques: false,
        formule: false,
        garde_meuble: false,
        calcul_tarif: false
    });

    // Admin settings (tarif au km, prix unitaires fournitures)
    const [settingsTarifs, setSettingsTarifs] = useState({
        tarif_km: 0,
        fournitures: {},
    });
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
                setSettingsTarifs(res.data?.tarifs || { tarif_km: 0, fournitures: {} });
            } catch {
                /* keep defaults 0 */
            }
        })();
    }, []);

    // ----- Offline-resilience: auto-save draft + network-error retry modal -----
    const draftKey = isEditing ? `prospect:${id}` : `prospect:new`;
    // Pass `emptyFormData` so the hook never persists/detects a draft that
    // is byte-identical to the initial empty form. Without this, the
    // "Brouillon non envoyé" dialog popped up on every fresh "Nouveau
    // prospect" click because React mounted with the empty state and the
    // hook auto-saved it after 1.5s.
    const draft = useFormDraft(
        draftKey,
        formData,
        setFormData,
        { initialData: isEditing ? undefined : pristineFormDataRef.current },
    );
    const resilient = useResilientSave();
    const [showDraftRestore, setShowDraftRestore] = useState(false);

    // Live presence (only for existing prospects)
    const presenceOthers = usePresence(isEditing ? 'prospect' : null, isEditing ? id : null);
    useEffect(() => {
        if (draft.hasDraft && !isEditing) setShowDraftRestore(true);
    }, [draft.hasDraft, isEditing]);

    useEffect(() => {
        if (isEditing) {
            fetchProspect();
            // Fetch inventory items count for validation
            axios.get(`${API_URL}/api/prospects/${id}/event-inventaire`, { withCredentials: true })
                .then(res => setInventaireItemsCount((res.data?.items || []).length))
                .catch(() => setInventaireItemsCount(0));
        } else {
            setInitialFormData(JSON.stringify(emptyFormData));
        }
    }, [id]);

    // Fetch agenda summary (camions+effectif déjà programmés) whenever dates change
    const isoToLocalDate = (iso) => {
        if (!iso) return '';
        // If it's already in YYYY-MM-DD format, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    useEffect(() => {
        const dates = new Set();
        const depDate = isoToLocalDate(formData.adresse_depart?.date);
        const arrDate = isoToLocalDate(formData.adresse_arrivee?.date);
        if (depDate) dates.add(depDate);
        if (arrDate) dates.add(arrDate);
        (formData.prepar_stock || []).forEach(ps => { if (ps.date) dates.add(ps.date); });
        if (dates.size === 0) {
            setAgendaSummary({});
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const params = { dates: Array.from(dates).join(',') };
                if (isEditing && formData.linked_client_id) {
                    params.exclude_client_id = formData.linked_client_id;
                }
                const resp = await axios.get(`${API_URL}/api/agenda-demenagement/summary`, {
                    params,
                    withCredentials: true,
                });
                if (!cancelled) setAgendaSummary(resp.data || {});
            } catch (e) {
                if (!cancelled) setAgendaSummary({});
            }
        })();
        return () => { cancelled = true; };
    }, [formData.adresse_depart?.date, formData.adresse_arrivee?.date, JSON.stringify((formData.prepar_stock || []).map(ps => ps.date)), isEditing, formData.linked_client_id]);

    const fetchProspect = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/prospects/${id}`, {
                withCredentials: true
            });
            const prospect = response.data;
            const loadedData = {
                nom: prospect.nom || '',
                telephone: prospect.telephone || '',
                email: prospect.email || '',
                source: prospect.source || '',
                departement: prospect.departement || '',
                statut: prospect.statut || 'nouveau',
                adresse_depart: prospect.adresse_depart ? {
                    ...emptyFormData.adresse_depart,
                    ...prospect.adresse_depart,
                    autorisation_stationnement: prospect.adresse_depart.autorisation_stationnement === true ? 'oui' : (prospect.adresse_depart.autorisation_stationnement === false && (prospect.adresse_depart.portage || !isExtended) ? 'non' : ''),
                    monte_meuble: prospect.adresse_depart.monte_meuble === true ? 'oui' : (prospect.adresse_depart.monte_meuble === false && (prospect.adresse_depart.portage || !isExtended) ? 'non' : '')
                } : emptyFormData.adresse_depart,
                adresse_arrivee: prospect.adresse_arrivee ? {
                    ...emptyFormData.adresse_arrivee,
                    ...prospect.adresse_arrivee,
                    autorisation_stationnement: prospect.adresse_arrivee.autorisation_stationnement === true ? 'oui' : (prospect.adresse_arrivee.autorisation_stationnement === false && (prospect.adresse_arrivee.portage || !isExtended) ? 'non' : ''),
                    monte_meuble: prospect.adresse_arrivee.monte_meuble === true ? 'oui' : (prospect.adresse_arrivee.monte_meuble === false && (prospect.adresse_arrivee.portage || !isExtended) ? 'non' : '')
                } : emptyFormData.adresse_arrivee,
                remarques: prospect.remarques || '',
                formules: prospect.formules || [],
                garde_meuble: prospect.garde_meuble || '',
                garde_meuble_duree: prospect.garde_meuble_duree || '',
                garde_meuble_mensualite: prospect.garde_meuble_mensualite || '',
                date_chargement: prospect.date_chargement || '',
                chargement_camions: prospect.chargement_camions || '',
                chargement_effectif: prospect.chargement_effectif || '',
                date_dechargement: prospect.date_dechargement || '',
                dechargement_camions: prospect.dechargement_camions || '',
                dechargement_effectif: prospect.dechargement_effectif || '',
                volume_devis: prospect.volume_devis || '',
                camion_20m3: prospect.camion_20m3 || '',
                camion_30m3: prospect.camion_30m3 || '',
                distance: prospect.distance || '',
                prepar_stock: prospect.prepar_stock || [],
                simulateur_devis: prospect.simulateur_devis || {
                    volume_devis: '', camion_20m3: '', camion_30m3: '', distance: '',
                    fournitures_active: false,
                    cartons_standards: '', cartons_livres: '', adhesif: '', papier_bulle: '', autres: ''
                }
            };
            setFormData(loadedData);
            setInitialFormData(JSON.stringify(loadedData));
            setUpdatedAt(prospect.updated_at || null);
        } catch (error) {
            toast.error('Erreur lors du chargement du prospect');
            navigate(backUrl);
        } finally {
            setLoading(false);
        }
    };

    const hasUnsavedChanges = () => {
        if (!initialFormData) return false;
        return JSON.stringify(formData) !== initialFormData;
    };

    const handleBack = () => {
        if (hasUnsavedChanges()) {
            setPendingNavigation(backUrl);
            setShowExitDialog(true);
        } else {
            navigate(backUrl);
        }
    };

    const handleConfirmExit = () => {
        setShowExitDialog(false);
        if (pendingNavigation) {
            navigate(pendingNavigation);
        }
    };

    const handleCancelExit = () => {
        setShowExitDialog(false);
        setPendingNavigation(null);
    };

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // ===== Calcul des couts: computed totals per formule =====
    const computeTotals = () => {
        const sim = formData.simulateur_devis || {};
        const cc = sim.calcul_couts || {};
        const num = (v) => {
            const n = parseFloat(v);
            return isNaN(n) ? 0 : n;
        };
        const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';

        const activeCols = FORMULE_COLUMNS.filter(c => (formData.formules || []).includes(c.key));

        // Informations de base required
        const baseKeys = ['volume_devis', 'camion_20m3', 'camion_30m3', 'distance'];
        const baseFilled = baseKeys.every(k => isFilled(sim[k]));

        // Fournitures cost + required-check
        const fournAct = sim.fournitures_active === true;
        const fournKeys = ['cartons_standards', 'cartons_livres', 'adhesif', 'papier_bulle', 'autres'];
        const fournFilled = !fournAct || fournKeys.every(k => isFilled(sim[k]));
        // Prices come from admin settings (Administration > Parametres > Tarifs)
        const f = settingsTarifs.fournitures || {};
        const priceCartonStd = num(f.carton_standard);
        const priceCartonLivre = num(f.carton_livre);
        const priceAdhesif = num(f.adhesif);
        const priceBulle100 = num(f.bulle_100m);
        // "autres" is a legacy free-entry field kept at unit price 1 euro
        // (no dedicated setting key). Admin can set cartons fragile/bouteille/verre
        // separately if needed in future.
        const fournituresCost = fournAct
            ? num(sim.cartons_standards) * priceCartonStd
              + num(sim.cartons_livres) * priceCartonLivre
              + num(sim.adhesif) * priceAdhesif
              + num(sim.papier_bulle) * priceBulle100
              + num(sim.autres) * 1
            : 0;

        // Tarif au km from admin settings (fallback 0 if not configured).
        // Distance cost = distance × (camion_20m3 + camion_30m3) × tarif_km.
        // Le multiplicateur "nombre de camions" reflète le fait qu'un AR est
        // facturé par camion engagé sur la prestation.
        const tarifKm = num(settingsTarifs.tarif_km);
        const nbCamions = num(sim.camion_20m3) + num(sim.camion_30m3);
        const distanceCost = num(sim.distance) * nbCamions * tarifKm;

        // Shared AOT / Monte-meuble fields
        const sharedRows = [
            { key: 'prix_aot_depart', show: formData.adresse_depart?.autorisation_stationnement === 'oui' },
            { key: 'prix_aot_arrivee', show: formData.adresse_arrivee?.autorisation_stationnement === 'oui' },
            { key: 'prix_monte_meuble_depart', show: formData.adresse_depart?.monte_meuble === 'oui' },
            { key: 'prix_monte_meuble_arrivee', show: formData.adresse_arrivee?.monte_meuble === 'oui' },
        ];
        let sharedCost = 0;
        let sharedFilled = true;
        sharedRows.forEach(r => {
            if (!r.show) return;
            const v = cc[r.key]?.value;
            if (!isFilled(v)) sharedFilled = false;
            sharedCost += num(v);
        });

        const totals = {};
        activeCols.forEach(({ key: formule }) => {
            const nbJours = parseInt(cc.nombre_jours?.[formule]) || 0;
            // per-formule required fields
            const perFormuleKeys = ['tarif_demenageur', 'panier', 'hotel', 'marge'];
            let perFilled = isFilled(cc.nombre_jours?.[formule]);
            perFormuleKeys.forEach(k => {
                if (!isFilled(cc[k]?.[formule])) perFilled = false;
            });
            // Effectifs jr per day
            for (let i = 1; i <= nbJours; i++) {
                if (!isFilled(cc[`effectifs_jr_${i}`]?.[formule])) perFilled = false;
            }

            const complete = baseFilled && fournFilled && sharedFilled && perFilled && nbJours > 0;

            const perCost = num(cc.tarif_demenageur?.[formule])
                + num(cc.panier?.[formule])
                + num(cc.hotel?.[formule])
                + num(cc.marge?.[formule]);

            const ht = fournituresCost + distanceCost + perCost + sharedCost;
            const ttc = ht * 1.2;
            totals[formule] = { ht, ttc, complete };
        });
        return totals;
    };

    // computeTotals reads formData & settingsTarifs via closure; memoize
    // against both to avoid recomputing the per-formule pricing matrix on
    // every keystroke while still reacting to admin settings updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const totals = useMemo(() => computeTotals(), [formData, settingsTarifs]);

    const updateAddressField = useCallback((type, field, value) => {
        setFormData(prev => {
            const updated = {
                ...prev,
                [type]: {
                    ...prev[type],
                    [field]: value
                }
            };
            // Sync address dates with calcul tarif dates (use local date to avoid TZ shift)
            if (field === 'date') {
                const toLocal = (iso) => {
                    if (!iso) return '';
                    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
                    const d = new Date(iso);
                    if (isNaN(d.getTime())) return '';
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                };
                const dateStr = toLocal(value);
                if (type === 'adresse_depart') updated.date_chargement = dateStr;
                if (type === 'adresse_arrivee') updated.date_dechargement = dateStr;
            }
            // Auto-sync département from departure address postal code (5-digit FR code)
            // Idf (75, 77, 78, 91-95) -> matching value ; tout autre département FR -> 'hors_idf'.
            if (type === 'adresse_depart' && field === 'adresse' && typeof value === 'string') {
                const m = value.match(/\b(\d{5})\b/);
                if (m) {
                    const cp2 = m[1].slice(0, 2);
                    const idfDepts = ['75', '77', '78', '91', '92', '93', '94', '95'];
                    const dept = idfDepts.includes(cp2) ? cp2 : 'hors_idf';
                    if (dept !== prev.departement) {
                        updated.departement = dept;
                    }
                }
            }
            return updated;
        });
    }, []);

    // Check if sections are complete - ALL fields must be filled
    // Note: on utilise un helper qui accepte "0" (chaine ou nombre) comme rempli,
    // sinon Portage=0 ou Etage=0 font passer la section en orange a tort.
    const _hasVal = (v) => v !== undefined && v !== null && String(v).trim() !== '';

    const isSourceComplete = () => {
        return _hasVal(formData.source) && _hasVal(formData.departement);
    };

    const isContactComplete = () => {
        return _hasVal(formData.nom) && _hasVal(formData.telephone) && _hasVal(formData.email);
    };

    const isDepartComplete = () => {
        const addr = formData.adresse_depart || {};
        return _hasVal(addr.date) && _hasVal(addr.adresse) && _hasVal(addr.etage) && _hasVal(addr.ascenseur) &&
               _hasVal(addr.portage) && _hasVal(addr.autorisation_stationnement) && _hasVal(addr.monte_meuble);
    };

    const isArriveeComplete = () => {
        const addr = formData.adresse_arrivee || {};
        return _hasVal(addr.date) && _hasVal(addr.adresse) && _hasVal(addr.etage) && _hasVal(addr.ascenseur) &&
               _hasVal(addr.portage) && _hasVal(addr.autorisation_stationnement) && _hasVal(addr.monte_meuble);
    };

    const isRemarquesComplete = () => {
        return _hasVal(formData.remarques);
    };

    const getStatusConfig = (statut) => {
        return STATUTS.find(s => s.value === statut) || STATUTS[0];
    };

    // Retourne la liste des sections incomplètes pour "Visite terminée"
    // Chaque entrée : { id, title, fields: [labels des champs manquants] }
    const getIncompleteSections = () => {
        const missing = [];
        // Helper: treat "0" as filled, but "" / null / undefined as empty
        const hasVal = (v) => v !== undefined && v !== null && String(v).trim() !== '';

        // Source
        const sourceFields = [];
        if (!hasVal(formData.source)) sourceFields.push('Source du prospect');
        if (!hasVal(formData.departement)) sourceFields.push('Département');
        if (sourceFields.length) missing.push({ id: 'source', title: 'Source', fields: sourceFields });

        // Contact
        const contactFields = [];
        if (!hasVal(formData.nom)) contactFields.push('Nom');
        if (!hasVal(formData.telephone)) contactFields.push('Téléphone');
        if (!hasVal(formData.email)) contactFields.push('Email');
        if (contactFields.length) missing.push({ id: 'contact', title: 'Contact', fields: contactFields });

        // Adresse de départ
        const ad = formData.adresse_depart || {};
        const depFields = [];
        if (!hasVal(ad.date)) depFields.push('Date chargement');
        if (!hasVal(ad.adresse)) depFields.push('Adresse');
        if (!hasVal(ad.etage)) depFields.push('Étage');
        if (!hasVal(ad.ascenseur)) depFields.push('Ascenseur');
        if (!hasVal(ad.portage)) depFields.push('Portage');
        if (!hasVal(ad.autorisation_stationnement)) depFields.push('Autorisation stationnement');
        if (!hasVal(ad.monte_meuble)) depFields.push('Monte-meuble');
        if (depFields.length) missing.push({ id: 'depart', title: 'Adresse de départ', fields: depFields });

        // Adresse d'arrivée
        const aa = formData.adresse_arrivee || {};
        const arrFields = [];
        if (!hasVal(aa.date)) arrFields.push('Date déchargement');
        if (!hasVal(aa.adresse)) arrFields.push('Adresse');
        if (!hasVal(aa.etage)) arrFields.push('Étage');
        if (!hasVal(aa.ascenseur)) arrFields.push('Ascenseur');
        if (!hasVal(aa.portage)) arrFields.push('Portage');
        if (!hasVal(aa.autorisation_stationnement)) arrFields.push('Autorisation stationnement');
        if (!hasVal(aa.monte_meuble)) arrFields.push('Monte-meuble');
        if (arrFields.length) missing.push({ id: 'arrivee', title: "Adresse d'arrivée", fields: arrFields });

        // ===== Sections étendues (uniquement en mode édition, car à la
        // création la plupart de ces champs ne sont pas encore affichés)
        if (isEditing) {
            // Formule
            const formules = Array.isArray(formData.formules) ? formData.formules : [];
            if (formules.length === 0) {
                missing.push({ id: 'formule', title: 'Formule', fields: ['Sélectionnez au moins une formule'] });
            }

            // Garde-meuble
            const gmFields = [];
            if (!hasVal(formData.garde_meuble)) {
                gmFields.push('Garde-meuble (Oui / Non)');
            } else if (formData.garde_meuble === 'oui') {
                if (!hasVal(formData.garde_meuble_duree)) gmFields.push('Durée du garde-meuble');
                if (!hasVal(formData.garde_meuble_mensualite)) gmFields.push('Mensualité');
            }
            if (gmFields.length) missing.push({ id: 'garde_meuble', title: 'Garde-meuble', fields: gmFields });

            // Calcul tarif (Informations de base + Fournitures + Calcul des coûts par formule)
            const sim = formData.simulateur_devis || {};
            const cc = sim.calcul_couts || {};
            const calculFields = [];

            // Pour les statuts "Devis estimatif" / "Listing telephone" / "Listing email"
            // on ne demande QUE le nombre de jours + les effectifs par jour.
            // Le reste (volume, camions, distance, fournitures, AOT, MM, tarif
            // déménageur, panier, hôtel, marge) reste facultatif.
            const lightStatuts = ['devis_estimatif', 'listing_telephone', 'listing_email'];
            const isLight = lightStatuts.includes(formData.statut_prospect);

            if (!isLight) {
                const baseLabels = {
                    volume_devis: 'Volume devis',
                    camion_20m3: 'Camion 20m³',
                    camion_30m3: 'Camion 30m³',
                    distance: 'Distance',
                };
                Object.entries(baseLabels).forEach(([k, label]) => {
                    if (!hasVal(sim[k])) calculFields.push(label);
                });

                // Fournitures : only required if active
                if (sim.fournitures_active === true) {
                    const fournLabels = {
                        cartons_standards: 'Cartons standards',
                        cartons_livres: 'Cartons livres',
                        adhesif: 'Adhésif',
                        papier_bulle: 'Papier bulle',
                        autres: 'Autres fournitures',
                    };
                    Object.entries(fournLabels).forEach(([k, label]) => {
                        if (!hasVal(sim[k])) calculFields.push(label);
                    });
                }

                // Shared AOT / Monte-meuble costs (when applicable)
                const sharedRows = [
                    { key: 'prix_aot_depart', label: 'Prix AOT départ', show: formData.adresse_depart?.autorisation_stationnement === 'oui' },
                    { key: 'prix_aot_arrivee', label: 'Prix AOT arrivée', show: formData.adresse_arrivee?.autorisation_stationnement === 'oui' },
                    { key: 'prix_monte_meuble_depart', label: 'Prix monte-meuble départ', show: formData.adresse_depart?.monte_meuble === 'oui' },
                    { key: 'prix_monte_meuble_arrivee', label: 'Prix monte-meuble arrivée', show: formData.adresse_arrivee?.monte_meuble === 'oui' },
                ];
                sharedRows.forEach(r => {
                    if (r.show && !hasVal(cc[r.key]?.value)) calculFields.push(r.label);
                });
            }

            // Per-formule fields (Calcul des coûts)
            // En mode "light" : seuls Nombre de jours + Effectifs sont requis.
            // En mode normal : on ajoute aussi tarif/panier/hotel/marge.
            if (formules.length > 0) {
                formules.forEach(formule => {
                    const labelF = formule.charAt(0).toUpperCase() + formule.slice(1);
                    const nbJours = parseInt(cc.nombre_jours?.[formule]);
                    if (!hasVal(cc.nombre_jours?.[formule])) {
                        calculFields.push(`${labelF} : Nombre de jours`);
                    }
                    if (!isLight) {
                        ['tarif_demenageur', 'panier', 'hotel', 'marge'].forEach(k => {
                            const prettyLabels = {
                                tarif_demenageur: 'Tarif déménageur',
                                panier: 'Panier',
                                hotel: 'Hôtel',
                                marge: 'Marge',
                            };
                            if (!hasVal(cc[k]?.[formule])) calculFields.push(`${labelF} : ${prettyLabels[k]}`);
                        });
                    }
                    if (!isNaN(nbJours) && nbJours > 0) {
                        for (let i = 1; i <= nbJours; i++) {
                            if (!hasVal(cc[`effectifs_jr_${i}`]?.[formule])) {
                                calculFields.push(`${labelF} : Effectifs jour ${i}`);
                            }
                        }
                    }
                });
            }

            if (calculFields.length) {
                missing.push({ id: 'calcul_tarif', title: 'Calcul tarif', fields: calculFields });
            }

            // Inventaire (items count)
            if (inventaireItemsCount !== null && inventaireItemsCount === 0) {
                missing.push({
                    id: 'inventaire',
                    title: 'Inventaire',
                    fields: ["Aucun meuble ajouté — rendez-vous dans l'onglet Inventaire pour le compléter"],
                });
            }
        }

        return missing;
    };

    const handleStatusChange = (value) => {
        setFormData(prev => ({ ...prev, statut: value }));
    };

    const scrollToFirstIncomplete = () => {
        const first = incompleteDialog.sections[0];
        if (!first) {
            setIncompleteDialog({ open: false, sections: [] });
            return;
        }
        // Ouvrir la section
        setOpenSections(prev => ({ ...prev, [first.id]: true }));
        setIncompleteDialog({ open: false, sections: [] });
        // Scroller après un petit délai (le temps que la section s'ouvre)
        setTimeout(() => {
            const el = sectionRefs.current[first.id];
            if (el && el.scrollIntoView) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    };

    const handleSubmit = async ({ silent = false } = {}) => {
        if (!formData.nom.trim()) {
            if (!silent) toast.error('Le nom est requis');
            return;
        }
        if (!formData.source) {
            if (!silent) toast.error('La source du prospect est requise');
            return;
        }

        // Extended mode validation is now soft (post-save dialog) — see end of try block.

        setSubmitting(true);

        try {
            // Prepare address data
            const prepareAddress = (addr) => {
                if (!addr.adresse && !addr.date && !addr.etage) return null;
                return {
                    date: addr.date || null,
                    adresse: addr.adresse || null,
                    etage: addr.etage || null,
                    ascenseur: addr.ascenseur || null,
                    portage: addr.portage || null,
                    autorisation_stationnement: addr.autorisation_stationnement === 'oui',
                    monte_meuble: addr.monte_meuble === 'oui'
                };
            };

            const payload = {
                nom: formData.nom.trim(),
                telephone: formData.telephone || null,
                email: formData.email || null,
                source: formData.source || null,
                departement: formData.departement || null,
                statut: isEditing ? formData.statut : (hasVisitParams ? visitType : 'nouveau'),
                adresse_depart: prepareAddress(formData.adresse_depart),
                adresse_arrivee: prepareAddress(formData.adresse_arrivee),
                remarques: formData.remarques || null,
                formules: formData.formules || [],
                garde_meuble: formData.garde_meuble || null,
                garde_meuble_duree: formData.garde_meuble === 'oui' ? (formData.garde_meuble_duree || null) : null,
                garde_meuble_mensualite: formData.garde_meuble === 'oui' && formData.garde_meuble_mensualite ? parseFloat(formData.garde_meuble_mensualite) : null,
                date_chargement: formData.date_chargement || null,
                chargement_camions: formData.chargement_camions || null,
                chargement_effectif: formData.chargement_effectif || null,
                date_dechargement: formData.date_dechargement || null,
                dechargement_camions: formData.dechargement_camions || null,
                dechargement_effectif: formData.dechargement_effectif || null,
                volume_devis: formData.volume_devis || null,
                camion_20m3: formData.camion_20m3 || null,
                camion_30m3: formData.camion_30m3 || null,
                distance: formData.distance || null,
                prepar_stock: formData.prepar_stock || [],
                simulateur_devis: formData.simulateur_devis || null
            };
            
            if (isEditing) {
                const putPayload = { ...payload, expected_updated_at: updatedAt };
                try {
                    await resilient.trySave(() => axios.put(`${API_URL}/api/prospects/${id}`, putPayload, { withCredentials: true })).then(r => { if (!r.ok) throw r.err; });
                } catch (err) {
                    if (err?.response?.status === 409) {
                        const d = err.response.data?.detail || {};
                        setConflict({
                            modified_by_name: d.modified_by_name || 'un autre utilisateur',
                            pendingPayload: payload,
                            isSilent: silent,
                        });
                        return; // user will choose Reload / Force
                    }
                    throw err;
                }
                // Also save the volume/inventory section if mounted (sync save)
                if (volumeSectionRef.current && typeof volumeSectionRef.current.save === 'function') {
                    try { await volumeSectionRef.current.save(); } catch { /* non-blocking */ }
                }
                if (!silent) toast.success('Prospect et inventaire sauvegardes');
                draft.clear();
                // Refresh updated_at for next save
                fetchProspect();
            } else {
                const saveRes = await resilient.trySave(() => axios.post(`${API_URL}/api/prospects`, payload, { withCredentials: true }));
                if (!saveRes.ok) throw saveRes.err;
                const prospectRes = saveRes.res;
                
                // Auto-create event if visit params from CarteRDV
                if (hasVisitParams && prospectRes.data?.id) {
                    try {
                        const typeLabel = visitType === 'visite_virtuelle' ? 'Visite virtuelle' : 'Visite présentiel';
                        const addr = formData.adresse_depart?.adresse || '';
                        const eventRes = await axios.post(`${API_URL}/api/events`, {
                            title: `${typeLabel} - ${formData.nom.trim()}`,
                            type: visitType,
                            date: visitDate,
                            start_time: visitStart || '09:00',
                            end_time: visitEnd || '10:00',
                            location: addr,
                            description: `Prospect: ${formData.nom.trim()}${formData.telephone ? ` | Tél: ${formData.telephone}` : ''}`,
                            prospect_id: prospectRes.data.id,
                        }, { withCredentials: true });
                        toast.success(`Prospect créé et ${typeLabel.toLowerCase()} planifiée`);

                        // Show email confirmation dialog if prospect has email
                        if (formData.email) {
                            setInitialFormData(JSON.stringify(formData));
                            setEmailConfirm({
                                eventId: eventRes.data?.id,
                                prospectId: prospectRes.data.id,
                                clientName: formData.nom.trim(),
                                clientEmail: formData.email,
                                date: visitDate,
                                heure: visitStart || '09:00',
                                visitType: visitType,
                                address: addr,
                            });
                            return; // Don't navigate yet - wait for email dialog
                        }
                    } catch {
                        toast.success('Prospect créé (erreur lors de la planification)');
                    }
                } else {
                    toast.success('Prospect créé avec succès');
                }
            }
            
            // Reset initial data to prevent exit dialog
            setInitialFormData(JSON.stringify(formData));
            // Successful save → clear the local draft for this form
            draft.clear();

            // Check completion status to show feedback
            const incompleteSections = getIncompleteSections();
            if (incompleteSections.length > 0) {
                setIncompleteDialog({ open: true, sections: incompleteSections });
                return; // do not navigate yet — let user fix things
            }
            // All sections are complete — show big success message
            setSuccessDialog(true);
            return; // do not navigate yet — wait for user to dismiss
        } catch (error) {
            // Network error → resilient.trySave already opened the offline modal.
            // Do not show a generic toast on top.
            if (isNetworkError(error)) {
                return;
            }
            console.error('Error:', error.response?.data);
            const detail = error.response?.data?.detail;
            if (Array.isArray(detail)) {
                toast.error(detail.map(d => d.msg).join(', '));
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'enregistrement');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Keep the ref up-to-date so the stable wrapper always calls the latest
    // closure of handleSubmit. This lets memoized children (VolumeSection)
    // skip re-renders on every keystroke.
    handleSubmitRef.current = handleSubmit;

    const handleSendConfirmationEmail = async () => {
        if (!emailConfirm) return;
        setSendingEmail(true);
        try {
            await axios.post(`${API_URL}/api/emails/send-rdv-confirmation`, {
                to_email: emailConfirm.clientEmail,
                client_name: emailConfirm.clientName,
                date: emailConfirm.date,
                heure: emailConfirm.heure,
                visit_type: emailConfirm.visitType,
                address: emailConfirm.address,
                event_id: emailConfirm.eventId,
                prospect_id: emailConfirm.prospectId,
            }, { withCredentials: true });
            toast.success('Le mail est bien envoyé merci 😎');
        } catch (err) {
            const msg = err.response?.data?.detail || "Erreur lors de l'envoi de l'email";
            toast.error(msg);
        } finally {
            setSendingEmail(false);
            setEmailConfirm(null);
            navigate('/agenda');
        }
    };

    const handleSkipEmail = () => {
        setEmailConfirm(null);
        navigate(backUrl);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-64">
                    <div className="w-8 h-8 border-4 border-[#1B365D] border-t-transparent rounded-full animate-spin"></div>
                </div>
            </Layout>
        );
    }

    const statusConfig = getStatusConfig(formData.statut);

    return (
        <Layout>
            <Toaster position="top-right" richColors />
            {resilient.Modal}

            {/* Restore draft prompt (offline survival) */}
            <AlertDialog open={showDraftRestore} onOpenChange={(v) => { if (!v) { draft.clear(); setShowDraftRestore(false); } }}>
                <AlertDialogContent data-testid="draft-restore-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1B365D]">Brouillon non envoyé</AlertDialogTitle>
                        <AlertDialogDescription>
                            Une saisie n'a pas été envoyée la dernière fois (probablement à cause d'une coupure réseau). Voulez-vous la restaurer pour terminer ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => { draft.clear(); setShowDraftRestore(false); }} data-testid="draft-discard">Ignorer</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { draft.restore(); setShowDraftRestore(false); }}
                            className="bg-[#1B365D] hover:bg-[#2A4A7A]"
                            data-testid="draft-restore"
                        >
                            Restaurer le brouillon
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            
            {/* Exit Confirmation Dialog */}
            <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1B365D]">
                            Modifications non enregistrées
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelExit}>
                            Rester sur la page
                        </AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleConfirmExit}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Quitter sans enregistrer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            
            <div className="max-w-6xl mx-auto animate-fadeIn">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 sticky top-0 z-30 bg-[#F3F4F6] py-3 -mt-4 lg:-mt-8 -mx-4 lg:-mx-8 px-4 lg:px-8 border-b border-gray-200 shadow-sm">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleBack}
                            className="text-[#1B365D] hover:bg-[#1B365D]/10"
                            data-testid="back-button"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 
                                className="text-2xl font-bold text-[#1B365D]"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                                data-testid="form-title"
                            >
                                {isEditing ? 'Modifier le Prospect' : 'Nouveau Prospect'}
                            </h1>
                            <p className="text-gray-500 text-sm">
                                {isEditing ? 'Modifiez les informations du prospect' : 'Remplissez les informations du nouveau prospect'}
                            </p>
                        </div>
                        {isEditing && <PresenceBadge others={presenceOthers} />}
                    </div>
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !canWriteProspect}
                        title={!canWriteProspect ? 'Accès refusé : permission Écriture requise' : ''}
                        className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-2 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prospect-form-submit"
                    >
                        <Save className="w-4 h-4" />
                        {submitting ? 'Enregistrement...' : (hasVisitParams && !isEditing ? 'Enregistrer et planifier' : 'Enregistrer')}
                    </Button>
                </div>

                {/* Visit Planning Banner - when coming from CarteRDV */}
                {hasVisitParams && !isEditing && (
                    <Card className="mb-6 border-2 border-[#1B365D]/20 bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/10" data-testid="visit-planning-banner">
                        <CardContent className="p-4">
                            <div className="flex flex-wrap items-center gap-3">
                                <CalendarIcon className="w-5 h-5 text-[#C9A962]" />
                                <span className="text-sm font-semibold text-[#1B365D]">Visite planifiée :</span>
                                <span className="text-sm text-[#1B365D]">
                                    {visitType === 'visite_virtuelle' ? 'Virtuelle' : 'Présentiel'} le {visitDate} de {visitStart || '09:00'} à {visitEnd || '10:00'}
                                </span>
                                {visitAddress && (
                                    <span className="text-sm text-gray-500 flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5" /> {visitAddress}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Remplissez les informations du prospect puis enregistrez pour créer le RDV automatiquement dans l'agenda.</p>
                        </CardContent>
                    </Card>
                )}

                {/* Status Card - Only show when editing */}
                {isEditing && (
                    <Card className="mb-6 border-2 border-[#C9A962]/30 bg-gradient-to-r from-[#1B365D]/5 to-[#C9A962]/10">
                        <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <Layers className="w-6 h-6 text-[#C9A962]" />
                                    <span className="text-lg font-semibold text-[#1B365D]">Statut du prospect</span>
                                </div>
                                <Select
                                    value={formData.statut}
                                    onValueChange={handleStatusChange}
                                >
                                    <SelectTrigger 
                                        className={`w-full sm:w-64 ${statusConfig.color} border font-medium`}
                                        data-testid="statut-select"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUTS.map((status) => (
                                            <SelectItem key={status.value} value={status.value}>
                                                {status.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Form Sections */}
                <div className="space-y-4">
                    {/* Source Section */}
                    <div ref={(el) => { sectionRefs.current.source = el; }}>
                    <AccordionSection 
                        title="Source" 
                        isOpen={openSections.source}
                        onToggle={() => toggleSection('source')}
                        completed={isSourceComplete()}
                    >
                        <FormRow label="Source du prospect" icon={Target} required>
                            <Select
                                value={formData.source}
                                onValueChange={(value) => setFormData(prev => ({ ...prev, source: value }))}
                            >
                                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="source-select">
                                    <SelectValue placeholder="Sélectionnez la source..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {SOURCES.map((source) => (
                                        <SelectItem key={source.value} value={source.value}>
                                            {source.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label="Département" icon={Building}>
                            <Select
                                value={formData.departement}
                                onValueChange={(value) => setFormData(prev => ({ ...prev, departement: value }))}
                            >
                                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="departement-select">
                                    <SelectValue placeholder="Sélectionnez le département..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {DEPARTEMENTS.map((dept) => (
                                        <SelectItem key={dept.value} value={dept.value}>
                                            {dept.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                    </AccordionSection>
                    </div>

                    {/* Contact Section */}
                    <div ref={(el) => { sectionRefs.current.contact = el; }}>
                    <AccordionSection 
                        title="Contact" 
                        isOpen={openSections.contact}
                        onToggle={() => toggleSection('contact')}
                        completed={isContactComplete()}
                    >
                        <FormRow label="Nom" icon={User} required>
                            <FastInput
                                defaultValue={formData.nom}
                                onCommit={(v) => setFormData(prev => ({ ...prev, nom: v }))}
                                placeholder="Saisir le nom"
                                className="bg-gray-50 border-gray-200"
                                data-testid="nom-input"
                            />
                        </FormRow>
                        <FormRow label="Téléphone" icon={Phone}>
                            <FastInput
                                type="tel"
                                inputMode="tel"
                                defaultValue={formData.telephone}
                                onCommit={(v) => setFormData(prev => ({ ...prev, telephone: v }))}
                                placeholder="Saisir le téléphone"
                                className="bg-gray-50 border-gray-200"
                                data-testid="telephone-input"
                            />
                        </FormRow>
                        <FormRow label="Email" icon={Mail}>
                            <FastInput
                                type="email"
                                inputMode="email"
                                defaultValue={formData.email}
                                onCommit={(v) => setFormData(prev => ({ ...prev, email: v }))}
                                placeholder="Saisir l'email"
                                className="bg-gray-50 border-gray-200"
                                data-testid="email-input"
                            />
                        </FormRow>
                    </AccordionSection>
                    </div>

                    {/* Adresse de départ Section */}
                    <div ref={(el) => { sectionRefs.current.depart = el; }}>
                    <AccordionSection 
                        title="Adresse de départ" 
                        isOpen={openSections.depart}
                        onToggle={() => toggleSection('depart')}
                        completed={isDepartComplete()}
                    >
                        <FormRow label="Date chargement" icon={CalendarIcon}>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start text-left font-normal bg-gray-50 border-gray-200"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                                        {formData.adresse_depart.date ? 
                                            format(new Date(formData.adresse_depart.date + (formData.adresse_depart.date.length === 10 ? 'T00:00:00' : '')), 'PPP', { locale: fr }) : 
                                            <span className="text-gray-400">Sélectionner une date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={formData.adresse_depart.date ? new Date(formData.adresse_depart.date + (formData.adresse_depart.date.length === 10 ? 'T00:00:00' : '')) : undefined}
                                        onSelect={(date) => updateAddressField('adresse_depart', 'date', date?.toISOString())}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            return d < today;
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </FormRow>
                        <FormRow label="Adresse" icon={MapPin}>
                            <AddressAutocomplete
                                value={formData.adresse_depart.adresse}
                                onChange={(value) => updateAddressField('adresse_depart', 'adresse', value)}
                                placeholder="Saisir l'adresse de départ"
                                testId="address-depart-input"
                            />
                        </FormRow>
                        <FormRow label="Étage">
                            <FastInput
                                inputMode="numeric"
                                defaultValue={formData.adresse_depart.etage}
                                onCommit={(v) => updateAddressField('adresse_depart', 'etage', v)}
                                placeholder="Saisir l'étage"
                                className="bg-gray-50 border-gray-200"
                            />
                        </FormRow>
                        <FormRow label="Ascenseur">
                            <Select
                                value={formData.adresse_depart.ascenseur}
                                onValueChange={(value) => updateAddressField('adresse_depart', 'ascenseur', value)}
                            >
                                <SelectTrigger className="bg-gray-50 border-gray-200">
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {ASCENSEURS.map((asc) => (
                                        <SelectItem key={asc.value} value={asc.value}>
                                            {asc.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label="Portage" required={isExtended}>
                            <FastInput
                                defaultValue={formData.adresse_depart.portage}
                                onCommit={(v) => updateAddressField('adresse_depart', 'portage', v)}
                                placeholder="Saisir le portage"
                                className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_depart.portage ? 'border-red-300' : ''}`}
                            />
                        </FormRow>
                        <FormRow label="Autorisation stationnement" required={isExtended}>
                            <Select
                                value={formData.adresse_depart.autorisation_stationnement}
                                onValueChange={(value) => updateAddressField('adresse_depart', 'autorisation_stationnement', value)}
                            >
                                <SelectTrigger className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_depart.autorisation_stationnement ? 'border-red-300' : ''}`}>
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {OUI_NON.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label="Monte meuble" required={isExtended}>
                            <Select
                                value={formData.adresse_depart.monte_meuble}
                                onValueChange={(value) => updateAddressField('adresse_depart', 'monte_meuble', value)}
                            >
                                <SelectTrigger className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_depart.monte_meuble ? 'border-red-300' : ''}`}>
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {OUI_NON.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                    </AccordionSection>
                    </div>

                    {/* Adresse d'arrivée Section */}
                    <div ref={(el) => { sectionRefs.current.arrivee = el; }}>
                    <AccordionSection 
                        title="Adresse d'arrivée" 
                        isOpen={openSections.arrivee}
                        onToggle={() => toggleSection('arrivee')}
                        completed={isArriveeComplete()}
                    >
                        <FormRow label="Date déchargement" icon={CalendarIcon}>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start text-left font-normal bg-gray-50 border-gray-200"
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 text-gray-400" />
                                        {formData.adresse_arrivee.date ? 
                                            format(new Date(formData.adresse_arrivee.date + (formData.adresse_arrivee.date.length === 10 ? 'T00:00:00' : '')), 'PPP', { locale: fr }) : 
                                            <span className="text-gray-400">Sélectionner une date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={formData.adresse_arrivee.date ? new Date(formData.adresse_arrivee.date + (formData.adresse_arrivee.date.length === 10 ? 'T00:00:00' : '')) : undefined}
                                        onSelect={(date) => {
                                            if (!date) return updateAddressField('adresse_arrivee', 'date', '');
                                            const y = date.getFullYear();
                                            const m = String(date.getMonth() + 1).padStart(2, '0');
                                            const d = String(date.getDate()).padStart(2, '0');
                                            updateAddressField('adresse_arrivee', 'date', `${y}-${m}-${d}`);
                                        }}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            // Pas de date anterieure a aujourd'hui
                                            if (d < today) return true;
                                            // Pas de date anterieure a la date de chargement
                                            const depRaw = formData.adresse_depart?.date;
                                            if (depRaw) {
                                                const dep = new Date(depRaw + (depRaw.length === 10 ? 'T00:00:00' : ''));
                                                dep.setHours(0, 0, 0, 0);
                                                if (d < dep) return true;
                                            }
                                            return false;
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </FormRow>
                        <FormRow label="Adresse" icon={MapPin}>
                            <AddressAutocomplete
                                value={formData.adresse_arrivee.adresse}
                                onChange={(value) => updateAddressField('adresse_arrivee', 'adresse', value)}
                                placeholder="Saisir l'adresse d'arrivée"
                                testId="address-arrivee-input"
                            />
                        </FormRow>
                        <FormRow label="Étage">
                            <FastInput
                                inputMode="numeric"
                                defaultValue={formData.adresse_arrivee.etage}
                                onCommit={(v) => updateAddressField('adresse_arrivee', 'etage', v)}
                                placeholder="Saisir l'étage"
                                className="bg-gray-50 border-gray-200"
                            />
                        </FormRow>
                        <FormRow label="Ascenseur">
                            <Select
                                value={formData.adresse_arrivee.ascenseur}
                                onValueChange={(value) => updateAddressField('adresse_arrivee', 'ascenseur', value)}
                            >
                                <SelectTrigger className="bg-gray-50 border-gray-200">
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {ASCENSEURS.map((asc) => (
                                        <SelectItem key={asc.value} value={asc.value}>
                                            {asc.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label="Portage" required={isExtended}>
                            <FastInput
                                defaultValue={formData.adresse_arrivee.portage}
                                onCommit={(v) => updateAddressField('adresse_arrivee', 'portage', v)}
                                placeholder="Saisir le portage"
                                className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_arrivee.portage ? 'border-red-300' : ''}`}
                            />
                        </FormRow>
                        <FormRow label="Autorisation stationnement" required={isExtended}>
                            <Select
                                value={formData.adresse_arrivee.autorisation_stationnement}
                                onValueChange={(value) => updateAddressField('adresse_arrivee', 'autorisation_stationnement', value)}
                            >
                                <SelectTrigger className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_arrivee.autorisation_stationnement ? 'border-red-300' : ''}`}>
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {OUI_NON.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label="Monte meuble" required={isExtended}>
                            <Select
                                value={formData.adresse_arrivee.monte_meuble}
                                onValueChange={(value) => updateAddressField('adresse_arrivee', 'monte_meuble', value)}
                            >
                                <SelectTrigger className={`bg-gray-50 border-gray-200 ${isExtended && !formData.adresse_arrivee.monte_meuble ? 'border-red-300' : ''}`}>
                                    <SelectValue placeholder="Sélectionner..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {OUI_NON.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormRow>
                    </AccordionSection>
                    </div>

                    {/* Remarques Section */}
                    <AccordionSection 
                        title="Remarque prospect" 
                        isOpen={openSections.remarques}
                        onToggle={() => toggleSection('remarques')}
                        completed={isRemarquesComplete()}
                    >
                        <div className="p-6">
                            <FastTextarea
                                defaultValue={formData.remarques}
                                onCommit={(v) => setFormData(prev => ({ ...prev, remarques: v }))}
                                placeholder="Saisir les remarques..."
                                rows={4}
                                className="bg-gray-50 border-gray-200"
                                data-testid="remarques-textarea"
                            />
                        </div>
                    </AccordionSection>

                    {/* Extended Sections - only when context=extended */}
                    {isExtended && (
                        <>
                        {/* Formule Section */}
                        <div ref={(el) => { sectionRefs.current.formule = el; }}>
                        <AccordionSection 
                            title="Formule" 
                            isOpen={openSections.formule}
                            onToggle={() => toggleSection('formule')}
                            completed={(formData.formules?.length || 0) > 0}
                        >
                            <div className="p-6 space-y-3">
                                {FORMULES.map((f) => {
                                    const isChecked = (formData.formules || []).includes(f.value);
                                    return (
                                        <button key={f.value}
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => {
                                                    const current = prev.formules || [];
                                                    const updated = isChecked 
                                                        ? current.filter(v => v !== f.value)
                                                        : [...current, f.value];
                                                    return { ...prev, formules: updated };
                                                });
                                            }}
                                            className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
                                                isChecked
                                                    ? 'border-[#1B365D] bg-[#1B365D]/5 ring-1 ring-[#1B365D]'
                                                    : 'border-gray-200 bg-white hover:border-[#1B365D]/30'
                                            }`}
                                            data-testid={`formule-${f.value}`}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                                isChecked ? 'border-[#1B365D] bg-[#1B365D]' : 'border-gray-300'
                                            }`}>
                                                {isChecked && <Check className="w-3 h-3 text-white" />}
                                            </div>
                                            <span className={`text-sm font-medium ${isChecked ? 'text-[#1B365D]' : 'text-gray-600'}`}>{f.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </AccordionSection>
                        </div>

                        {/* Garde Meuble Section */}
                        <div ref={(el) => { sectionRefs.current.garde_meuble = el; }}>
                        <AccordionSection 
                            title="Garde meuble" 
                            isOpen={openSections.garde_meuble}
                            onToggle={() => toggleSection('garde_meuble')}
                            completed={!!formData.garde_meuble}
                        >
                            <div className="p-6 space-y-0">
                                <FormRow label="Garde meuble" icon={Package}>
                                    <Select value={formData.garde_meuble || '_none'} onValueChange={(v) => setFormData(prev => ({ ...prev, garde_meuble: v === '_none' ? '' : v }))}>
                                        <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="garde-meuble-select">
                                            <SelectValue placeholder="Selectionner" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none">Selectionner</SelectItem>
                                            {GARDE_MEUBLE_OPTIONS.map(o => (
                                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormRow>
                                {formData.garde_meuble === 'oui' && (
                                <>
                                <FormRow label="Duree" icon={Clock}>
                                    <FastInput
                                        defaultValue={formData.garde_meuble_duree || ''}
                                        onCommit={(v) => setFormData(prev => ({ ...prev, garde_meuble_duree: v }))}
                                        placeholder="Ex: 3 mois"
                                        className="bg-gray-50 border-gray-200"
                                        data-testid="garde-meuble-duree"
                                    />
                                </FormRow>
                                <FormRow label="Mensualite EUR" icon={DollarSign}>
                                    <FastInput
                                        type="number" min="0" step="0.01"
                                        inputMode="decimal"
                                        defaultValue={formData.garde_meuble_mensualite || ''}
                                        onCommit={(v) => setFormData(prev => ({ ...prev, garde_meuble_mensualite: v }))}
                                        placeholder="0"
                                        className="bg-gray-50 border-gray-200"
                                        data-testid="garde-meuble-mensualite"
                                    />
                                </FormRow>
                                </>
                                )}
                            </div>
                        </AccordionSection>
                        </div>

                        {/* Calcul Tarif Section */}
                        <div ref={(el) => { sectionRefs.current.calcul_tarif = el; }}>
                        <AccordionSection 
                            title="Calcul tarif" 
                            isOpen={openSections.calcul_tarif}
                            onToggle={() => toggleSection('calcul_tarif')}
                            completed={!!((formData.adresse_depart?.date) || formData.prepar_stock?.length)}
                            headerAction={
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 px-3 text-xs font-semibold border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
                                    data-testid="add-prepar-stock-btn"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        prepar_stock: [...(prev.prepar_stock || []), { date: '', camions: '', effectif: '' }]
                                    }))}
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1" />
                                    Prepar/Stock
                                </Button>
                            }
                        >
                            <div className="p-6 space-y-0">
                                {(() => {
                                    const depDate = isoToLocalDate(formData.adresse_depart?.date);
                                    const arrDate = isoToLocalDate(formData.adresse_arrivee?.date);
                                    return (
                                    <>
                                {/* Date chargement header */}
                                <div className="bg-blue-50 px-4 py-2.5 -mx-6 mb-0 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-blue-600">Date chargement</span>
                                    <span className="text-sm text-gray-700">{depDate || '-'}</span>
                                </div>
                                <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Nombre de camion reserve</span>
                                    <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid="chargement-camions-agenda">
                                        {depDate ? (agendaSummary[depDate]?.camions ?? 0) : '-'}
                                    </div>
                                </div>
                                <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Effectif reserve</span>
                                    <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid="chargement-effectif-agenda">
                                        {depDate ? (agendaSummary[depDate]?.effectif ?? 0) : '-'}
                                    </div>
                                </div>

                                {/* Date dechargement header */}
                                <div className="bg-blue-50 px-4 py-2.5 -mx-6 mt-2 mb-0 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-blue-600">Date dechargement</span>
                                    <span className="text-sm text-gray-700">{arrDate || '-'}</span>
                                </div>
                                <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Nombre de camion reserve</span>
                                    <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid="dechargement-camions-agenda">
                                        {arrDate ? (agendaSummary[arrDate]?.camions ?? 0) : '-'}
                                    </div>
                                </div>
                                <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                    <span className="text-sm text-gray-600">Effectif reserve</span>
                                    <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid="dechargement-effectif-agenda">
                                        {arrDate ? (agendaSummary[arrDate]?.effectif ?? 0) : '-'}
                                    </div>
                                </div>
                                    </>
                                    );
                                })()}

                                {/* Prepar/Stock dynamic sections */}
                                {(formData.prepar_stock || []).map((ps, idx) => (
                                    <div key={idx}>
                                        <div className="bg-amber-50 px-4 py-2.5 -mx-6 mt-2 mb-0 flex items-center justify-between">
                                            <span className="text-sm font-semibold text-amber-700">Prepar/Stock {idx + 1}</span>
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    prepar_stock: prev.prepar_stock.filter((_, i) => i !== idx)
                                                }))}
                                                className="text-red-400 hover:text-red-600 transition-colors"
                                                data-testid={`remove-prepar-stock-${idx}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                            <span className="text-sm text-gray-600">Date</span>
                                            <Input type="date" value={ps.date || ''}
                                                onChange={(e) => {
                                                    const updated = [...formData.prepar_stock];
                                                    updated[idx] = { ...updated[idx], date: e.target.value };
                                                    setFormData(prev => ({ ...prev, prepar_stock: updated }));
                                                }}
                                                className="w-48 bg-gray-50 border-gray-200 text-sm" data-testid={`prepar-stock-date-${idx}`} />
                                        </div>
                                        <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                            <span className="text-sm text-gray-600">Nombre de camion reserve</span>
                                            <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid={`prepar-stock-camions-agenda-${idx}`}>
                                                {ps.date ? (agendaSummary[ps.date]?.camions ?? 0) : '-'}
                                            </div>
                                        </div>
                                        <div className="py-3 flex items-center justify-between border-b border-gray-100">
                                            <span className="text-sm text-gray-600">Effectif reserve</span>
                                            <div className="w-48 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid={`prepar-stock-effectif-agenda-${idx}`}>
                                                {ps.date ? (agendaSummary[ps.date]?.effectif ?? 0) : '-'}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* End dechargement */}

                                {/* ===== SIMULATEUR DE DEVIS ===== */}
                                <div className="mt-6 -mx-6 border-t-2 border-gray-200">
                                    {/* Prix cards - only show selected formules */}
                                    {(formData.formules?.length || 0) > 0 && (
                                    <div className={`grid gap-3 px-6 pt-6 pb-4 ${formData.formules.length === 1 ? 'grid-cols-1' : formData.formules.length === 2 ? 'grid-cols-2' : formData.formules.length === 3 ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-4'}`}>
                                        {FORMULE_COLUMNS.filter(c => (formData.formules || []).includes(c.key)).map(({ key, label, border }) => {
                                            const t = totals[key];
                                            return (
                                            <div key={key} className={`bg-blue-50/60 rounded-lg border border-blue-200/60 border-l-4 ${border} p-4 text-center`} data-testid={`prix-card-${key}`}>
                                                <span className="text-xs font-bold text-blue-600 tracking-wider">{label}</span>
                                                <p className="text-xl font-bold text-[#1B365D] mt-1">
                                                    {t?.complete ? `${t.ttc.toFixed(2).replace('.', ',')} €` : '— €'}
                                                </p>
                                                <span className="text-xs text-gray-400">TTC</span>
                                            </div>
                                            );
                                        })}
                                    </div>
                                    )}

                                    {/* Informations de base */}
                                    <div className="px-6 pb-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Calculator className="w-4 h-4 text-[#1B365D]/60" />
                                            <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Informations de base</h4>
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            {[
                                                { key: 'volume_devis', label: 'Volume devis' },
                                                { key: 'camion_20m3', label: 'Camion 20 m\u00b3' },
                                                { key: 'camion_30m3', label: 'Camion 30 m\u00b3' },
                                                { key: 'distance', label: 'Distance (km)' },
                                            ].map(({ key, label }) => (
                                                <div key={key}>
                                                    <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                                                    <FastInput
                                                        type="number" min="0"
                                                        inputMode="decimal"
                                                        defaultValue={formData.simulateur_devis?.[key] || ''}
                                                        onCommit={(v) => setFormData(prev => ({
                                                            ...prev,
                                                            simulateur_devis: { ...prev.simulateur_devis, [key]: v }
                                                        }))}
                                                        placeholder="0"
                                                        className="bg-gray-50 border-gray-200 text-sm"
                                                        data-testid={`sim-${key}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Fournitures */}
                                    <div className="px-6 pb-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <Gift className="w-4 h-4 text-[#1B365D]/60" />
                                                <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Fournitures</h4>
                                            </div>
                                            <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-testid="fournitures-toggle">
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        simulateur_devis: { ...prev.simulateur_devis, fournitures_active: false }
                                                    }))}
                                                    className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                                                        !formData.simulateur_devis?.fournitures_active
                                                            ? 'bg-white text-[#1B365D] shadow-sm'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                    data-testid="fournitures-non"
                                                >Non</button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        simulateur_devis: { ...prev.simulateur_devis, fournitures_active: true }
                                                    }))}
                                                    className={`px-4 py-1.5 text-xs font-semibold transition-colors ${
                                                        formData.simulateur_devis?.fournitures_active
                                                            ? 'bg-[#C9A962] text-white shadow-sm'
                                                            : 'text-gray-400 hover:text-gray-600'
                                                    }`}
                                                    data-testid="fournitures-oui"
                                                >Oui</button>
                                            </div>
                                        </div>
                                        {formData.simulateur_devis?.fournitures_active && (
                                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                                {[
                                                    { key: 'cartons_standards', label: 'Cartons standards' },
                                                    { key: 'cartons_livres', label: 'Cartons livres' },
                                                    { key: 'adhesif', label: 'Adhesif' },
                                                    { key: 'papier_bulle', label: 'Papier bulle' },
                                                    { key: 'autres', label: 'Autres' },
                                                ].map(({ key, label }) => (
                                                    <div key={key}>
                                                        <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                                                        <FastInput
                                                            type="number" min="0"
                                                            inputMode="decimal"
                                                            defaultValue={formData.simulateur_devis?.[key] || ''}
                                                            onCommit={(v) => setFormData(prev => ({
                                                                ...prev,
                                                                simulateur_devis: { ...prev.simulateur_devis, [key]: v }
                                                            }))}
                                                            placeholder="0"
                                                            className="bg-gray-50 border-gray-200 text-sm"
                                                            data-testid={`sim-${key}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* ===== CALCUL DES COUTS TABLE ===== */}
                                    <div className="px-6 pb-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <DollarSign className="w-4 h-4 text-[#1B365D]/60" />
                                            <h4 className="text-sm font-bold text-[#1B365D] uppercase tracking-wide">Calcul des couts</h4>
                                        </div>
                                        {(formData.formules?.length || 0) === 0 ? (
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800 flex items-start gap-3" data-testid="calcul-couts-empty">
                                                <Target className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="font-semibold">Aucune formule sélectionnée</p>
                                                    <p className="mt-0.5 text-amber-700">Sélectionnez au moins une formule dans la section <strong>Formule</strong> pour afficher le tableau de calcul des coûts.</p>
                                                </div>
                                            </div>
                                        ) : (
                                        (() => {
                                            const activeCols = FORMULE_COLUMNS.filter(c => (formData.formules || []).includes(c.key));
                                            const colCount = activeCols.length + 1;
                                            const gridClass = `grid-cols-${colCount}`;
                                            return (
                                        <div className="bg-gray-50/50 rounded-xl border border-gray-200 overflow-hidden" data-testid="calcul-couts-table">
                                            {/* Header */}
                                            <div className={`grid bg-[#1B365D]/5 border-b border-gray-200`} style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                <div className="px-4 py-3"><span className="text-xs font-bold text-[#1B365D] uppercase tracking-wider">Ligne</span></div>
                                                {activeCols.map(({ key, label, color }) => (
                                                    <div key={key} className="px-3 py-3 text-center"><span className={`text-xs font-bold ${color} uppercase tracking-wider`}>{label}</span></div>
                                                ))}
                                            </div>
                                            {/* Nombre de jours row */}
                                            <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                <div className="px-4 py-3 flex items-center">
                                                    <span className="text-sm text-gray-700">Nombre de jours</span>
                                                </div>
                                                {activeCols.map(({ key: formule }) => (
                                                    <div key={formule} className="px-2 py-2 flex items-center justify-center">
                                                        <FastInput
                                                            type="number" min="0" step="1"
                                                            inputMode="numeric"
                                                            defaultValue={formData.simulateur_devis?.calcul_couts?.nombre_jours?.[formule] || ''}
                                                            onCommit={(v) => setFormData(prev => {
                                                                const sim = { ...prev.simulateur_devis };
                                                                if (!sim.calcul_couts) sim.calcul_couts = {};
                                                                if (!sim.calcul_couts.nombre_jours) sim.calcul_couts.nombre_jours = {};
                                                                sim.calcul_couts.nombre_jours = { ...sim.calcul_couts.nombre_jours, [formule]: v };
                                                                return { ...prev, simulateur_devis: sim };
                                                            })}
                                                            placeholder=""
                                                            className="bg-white border-gray-200 text-sm text-center h-9"
                                                            data-testid={`cout-nombre_jours-${formule}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Dynamic Effectifs jr rows */}
                                            {(() => {
                                                const maxJours = Math.max(0, ...activeCols.map(({ key }) => parseInt(formData.simulateur_devis?.calcul_couts?.nombre_jours?.[key]) || 0));
                                                return Array.from({ length: maxJours }, (_, i) => {
                                                    const jrKey = `effectifs_jr_${i + 1}`;
                                                    return (
                                                        <div key={jrKey} className="grid border-b border-gray-100 bg-blue-50/30" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                            <div className="px-4 py-3 flex items-center">
                                                                <span className="text-sm text-blue-600 font-medium">Effectifs jr {i + 1}</span>
                                                            </div>
                                                            {activeCols.map(({ key: formule }) => {
                                                                const nbJours = parseInt(formData.simulateur_devis?.calcul_couts?.nombre_jours?.[formule]) || 0;
                                                                if (i >= nbJours) return <div key={formule} />;
                                                                return (
                                                                    <div key={formule} className="px-2 py-2 flex items-center justify-center">
                                                                        <FastInput
                                                                            type="number" min="0" step="1"
                                                                            inputMode="numeric"
                                                                            defaultValue={formData.simulateur_devis?.calcul_couts?.[jrKey]?.[formule] || ''}
                                                                            onCommit={(v) => setFormData(prev => {
                                                                                const sim = { ...prev.simulateur_devis };
                                                                                if (!sim.calcul_couts) sim.calcul_couts = {};
                                                                                const row = { ...(sim.calcul_couts[jrKey] || {}), [formule]: v };
                                                                                sim.calcul_couts = { ...sim.calcul_couts, [jrKey]: row };
                                                                                return { ...prev, simulateur_devis: sim };
                                                                            })}
                                                                            placeholder=""
                                                                            className="bg-white border-gray-200 text-sm text-center h-9"
                                                                            data-testid={`cout-${jrKey}-${formule}`}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                            {/* Remaining multi-column rows */}
                                            {[
                                                { key: 'tarif_demenageur', label: 'Tarif demenageur (\u20ac)' },
                                                { key: 'panier', label: 'Panier (\u20ac)' },
                                                { key: 'hotel', label: 'Hotel (\u20ac)' },
                                            ].map(({ key, label }) => (
                                                <div key={key} className="grid border-b border-gray-100" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3 flex items-center">
                                                        <span className="text-sm text-gray-700">{label}</span>
                                                    </div>
                                                    {activeCols.map(({ key: formule }) => (
                                                        <div key={formule} className="px-2 py-2 flex items-center justify-center">
                                                            <FastInput
                                                                type="number" min="0" step="0.01"
                                                                inputMode="decimal"
                                                                defaultValue={formData.simulateur_devis?.calcul_couts?.[key]?.[formule] || ''}
                                                                onCommit={(v) => setFormData(prev => {
                                                                    const sim = { ...prev.simulateur_devis };
                                                                    if (!sim.calcul_couts) sim.calcul_couts = {};
                                                                    const row = { ...(sim.calcul_couts[key] || {}), [formule]: v };
                                                                    sim.calcul_couts = { ...sim.calcul_couts, [key]: row };
                                                                    return { ...prev, simulateur_devis: sim };
                                                                })}
                                                                placeholder=""
                                                                className="bg-white border-gray-200 text-sm text-center h-9"
                                                                data-testid={`cout-${key}-${formule}`}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                            {/* Single-field conditional rows (AOT / Monte-meuble) */}
                                            {[
                                                { key: 'prix_aot_depart', label: 'Prix AOT depart', show: formData.adresse_depart?.autorisation_stationnement === 'oui' },
                                                { key: 'prix_aot_arrivee', label: 'Prix AOT arrivee', show: formData.adresse_arrivee?.autorisation_stationnement === 'oui' },
                                                { key: 'prix_monte_meuble_depart', label: 'Prix Monte-meuble depart', show: formData.adresse_depart?.monte_meuble === 'oui' },
                                                { key: 'prix_monte_meuble_arrivee', label: 'Prix Monte-meuble arrivee', show: formData.adresse_arrivee?.monte_meuble === 'oui' },
                                            ].filter(r => r.show).map(({ key, label }) => (
                                                <div key={key} className="grid border-b border-gray-100 bg-blue-50/40" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                    <div className="px-4 py-3 flex items-center">
                                                        <span className="text-sm text-blue-600 font-medium italic">{label}</span>
                                                    </div>
                                                    <div className="px-2 py-2 flex items-center justify-center" style={{ gridColumn: `2 / ${activeCols.length + 2}` }}>
                                                        <FastInput
                                                            type="number" min="0" step="0.01"
                                                            inputMode="decimal"
                                                            defaultValue={formData.simulateur_devis?.calcul_couts?.[key]?.value || ''}
                                                            onCommit={(v) => setFormData(prev => {
                                                                const sim = { ...prev.simulateur_devis };
                                                                if (!sim.calcul_couts) sim.calcul_couts = {};
                                                                sim.calcul_couts = { ...sim.calcul_couts, [key]: { value: v } };
                                                                return { ...prev, simulateur_devis: sim };
                                                            })}
                                                            placeholder=""
                                                            className="bg-white border-gray-200 text-sm text-center h-9 max-w-[240px] mx-auto"
                                                            data-testid={`cout-${key}`}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Marge row */}
                                            <div className="grid border-b border-gray-100" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                <div className="px-4 py-3 flex items-center">
                                                    <span className="text-sm text-gray-700">Marge (\u20ac)</span>
                                                </div>
                                                {activeCols.map(({ key: formule }) => (
                                                    <div key={formule} className="px-2 py-2 flex items-center justify-center">
                                                        <FastInput
                                                            type="number" min="0" step="0.01"
                                                            inputMode="decimal"
                                                            defaultValue={formData.simulateur_devis?.calcul_couts?.marge?.[formule] || ''}
                                                            onCommit={(v) => setFormData(prev => {
                                                                const sim = { ...prev.simulateur_devis };
                                                                if (!sim.calcul_couts) sim.calcul_couts = {};
                                                                const row = { ...(sim.calcul_couts.marge || {}), [formule]: v };
                                                                sim.calcul_couts = { ...sim.calcul_couts, marge: row };
                                                                return { ...prev, simulateur_devis: sim };
                                                            })}
                                                            placeholder=""
                                                            className="bg-white border-gray-200 text-sm text-center h-9"
                                                            data-testid={`cout-marge-${formule}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Total HT */}
                                            <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                <div className="px-4 py-3 flex items-center">
                                                    <span className="text-sm font-bold text-[#1B365D]">Total HT</span>
                                                </div>
                                                {activeCols.map(({ key }) => {
                                                    const t = totals[key];
                                                    return (
                                                        <div key={key} className="px-3 py-3 text-center" data-testid={`total-ht-${key}`}>
                                                            <span className="text-sm font-bold text-[#1B365D]">
                                                                {t?.complete ? `${t.ht.toFixed(2).replace('.', ',')} €` : '— €'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {/* Total TTC */}
                                            <div className="grid bg-[#1B365D]/5" style={{ gridTemplateColumns: `minmax(160px, 1fr) repeat(${activeCols.length}, 1fr)` }}>
                                                <div className="px-4 py-3 flex items-center">
                                                    <span className="text-sm font-bold text-blue-600">Total TTC</span>
                                                </div>
                                                {activeCols.map(({ key }) => {
                                                    const t = totals[key];
                                                    return (
                                                        <div key={key} className="px-3 py-3 text-center" data-testid={`total-ttc-${key}`}>
                                                            <span className="text-sm font-bold text-blue-600">
                                                                {t?.complete ? `${t.ttc.toFixed(2).replace('.', ',')} €` : '— €'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                            );
                                        })()
                                        )}
                                    </div>
                                </div>
                            </div>
                        </AccordionSection>
                        </div>
                        </>
                    )}
                </div>

                {/* Volume Section - only in extended context */}
                {isExtended && isEditing && (
                    <div className="mt-6" ref={(el) => { sectionRefs.current.inventaire = el; }}>
                        <VolumeSection
                            prospectId={id}
                            ref={volumeSectionRef}
                            onExternalSave={stableExternalSave}
                        />
                    </div>
                )}

                {/* Bottom Save Button (Mobile) */}
                <div className="mt-6 pb-6 sm:hidden">
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !canWriteProspect}
                        title={!canWriteProspect ? 'Accès refusé : permission Écriture requise' : ''}
                        className="w-full bg-[#C9A962] hover:bg-[#B8984F] text-white gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Save className="w-4 h-4" />
                        {submitting ? 'Enregistrement...' : (hasVisitParams && !isEditing ? 'Enregistrer et planifier' : 'Enregistrer')}
                    </Button>
                </div>
            </div>

            {/* ========= EMAIL CONFIRMATION DIALOG ========= */}
            <Dialog open={!!emailConfirm} onOpenChange={(open) => { if (!open) handleSkipEmail(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Mail className="w-5 h-5 text-[#C9A962]" />
                            Envoyer un email de confirmation ?
                        </DialogTitle>
                        <DialogDescription>
                            Souhaitez-vous envoyer un email de confirmation de rendez-vous à <strong>{emailConfirm?.clientName}</strong> ?
                        </DialogDescription>
                    </DialogHeader>
                    {emailConfirm && (
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm border" data-testid="email-confirm-details">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Email :</span>
                                <span className="font-medium text-[#1B365D]">{emailConfirm.clientEmail}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                                <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Date :</span>
                                <span className="font-medium text-[#1B365D]">{emailConfirm.date} à {emailConfirm.heure}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-gray-500">Type :</span>
                                <span className="font-medium text-[#1B365D]">
                                    {emailConfirm.visitType === 'visite_virtuelle' ? 'Visite virtuelle' : 'Visite présentiel'}
                                </span>
                            </div>
                            {emailConfirm.address && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                                    <span className="text-gray-500">Adresse :</span>
                                    <span className="font-medium text-[#1B365D]">{emailConfirm.address}</span>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={handleSkipEmail} data-testid="skip-email-btn">
                            Ne pas envoyer
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSendConfirmationEmail}
                            disabled={sendingEmail}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5"
                            data-testid="send-confirmation-btn"
                        >
                            <Send className="w-4 h-4" />
                            {sendingEmail ? 'Envoi en cours...' : 'Envoyer la confirmation'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========= FORM INCOMPLETE INFORMATIONAL DIALOG ========= */}
            <Dialog open={incompleteDialog.open} onOpenChange={(open) => { if (!open) { setIncompleteDialog({ open: false, sections: [] }); navigate(backUrl); } }}>
                <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="incomplete-sections-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Target className="w-5 h-5 text-amber-500" />
                            Formulaire incomplet
                        </DialogTitle>
                        <DialogDescription>
                            Vos modifications ont bien été enregistrées, mais il reste {incompleteDialog.sections.length} section{incompleteDialog.sections.length > 1 ? 's' : ''} à compléter.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 space-y-3 text-sm" data-testid="incomplete-sections-list">
                        {incompleteDialog.sections.map((s) => (
                            <div key={s.id}>
                                <div className="flex items-center gap-2 font-semibold text-amber-700 mb-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    {s.title}
                                </div>
                                <ul className="ml-5 space-y-0.5">
                                    {(s.fields || []).map((f, i) => (
                                        <li key={i} className="text-amber-700/90 text-[13px] flex items-center gap-1.5">
                                            <span className="text-amber-400">—</span> {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => { setIncompleteDialog({ open: false, sections: [] }); navigate(backUrl); }} data-testid="incomplete-cancel-btn">
                            Quitter quand même
                        </Button>
                        <Button
                            type="button"
                            onClick={scrollToFirstIncomplete}
                            className="bg-[#C9A962] hover:bg-[#B8984F] text-white gap-1.5"
                            data-testid="incomplete-scroll-btn"
                        >
                            <ChevronDown className="w-4 h-4" />
                            Compléter maintenant
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========= INVENTAIRE COMPLET - BIG SUCCESS DIALOG ========= */}
            <Dialog open={successDialog} onOpenChange={(open) => { if (!open) { setSuccessDialog(false); navigate(backUrl); } }}>
                <DialogContent className="sm:max-w-xl" data-testid="inventaire-complete-dialog">
                    <div className="flex flex-col items-center text-center py-6 px-2">
                        <div className="relative mb-4">
                            <div className="absolute inset-0 rounded-full bg-emerald-200/40 animate-ping" />
                            <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
                                <CheckCircle2 className="w-12 h-12 text-white" />
                            </div>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-emerald-700 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="inventaire-complete-title">
                            Inventaire complet !
                        </h2>
                        <p className="text-base sm:text-lg text-[#1B365D] max-w-md mb-1 font-medium">
                            N'oubliez pas d'envoyer le devis
                        </p>
                        <p className="text-sm text-gray-500 max-w-md mb-6">
                            dans les meilleurs délais
                        </p>
                        <div className="flex gap-3 flex-wrap justify-center">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => { setSuccessDialog(false); navigate(backUrl); }}
                                data-testid="inventaire-complete-close-btn"
                            >
                                Fermer
                            </Button>
                            {id && (
                                <Button
                                    type="button"
                                    onClick={() => { setSuccessDialog(false); navigate(`/prospects/${id}/devis/new`); }}
                                    className="bg-[#C9A962] hover:bg-[#B8984F] text-white"
                                    data-testid="inventaire-complete-devis-btn"
                                >
                                    Créer le devis maintenant
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <ConflictDialog
                open={!!conflict}
                conflict={conflict}
                onCancel={() => setConflict(null)}
                onReload={async () => {
                    setConflict(null);
                    await fetchProspect();
                    toast.message('Fiche rechargée avec les dernières modifications');
                }}
                onForce={async () => {
                    if (!conflict) return;
                    const { pendingPayload, isSilent } = conflict;
                    setConflict(null);
                    try {
                        // Re-fetch the latest updated_at, then force-save (skip lock)
                        const latest = await axios.get(`${API_URL}/api/prospects/${id}`, { withCredentials: true });
                        await axios.put(
                            `${API_URL}/api/prospects/${id}`,
                            { ...pendingPayload, expected_updated_at: latest.data?.updated_at },
                            { withCredentials: true },
                        );
                        if (volumeSectionRef.current && typeof volumeSectionRef.current.save === 'function') {
                            try { await volumeSectionRef.current.save(); } catch { /* non-blocking */ }
                        }
                        if (!isSilent) toast.success('Votre version a été enregistrée');
                        draft.clear();
                        fetchProspect();
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Erreur');
                    }
                }}
            />
        </Layout>
    );
}
